import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { useSelector } from 'react-redux'

import { useAuth, useProjectAccess } from '@/hooks'
import { useGetDataForExport } from '@/hooks/exportDropdown'
import type { ExportData } from '@/hooks/exportDropdown/types'
import { SEO_TAGS_PERMISSION } from '@/permissions/project/definitions'
import { getBlockContent } from '@/utils'
import { getProjectWxr, type WxrPageItem } from '@/utils/wordpress'
import { serializeWp } from './serializeWp'

interface Options {
  nestUnderHome: boolean
  withContent: boolean
}

export const useExportWP = () => {
  const { getFlattenExportData } = useGetDataForExport()

  const name = useSelector(migrationProjectSelector.getProjectTitle)
  const updatedAt = useSelector(migrationProjectSelector.getProjectUpdatedAt)

  const { emailAddress } = useAuth()
  const { hasAccess } = useProjectAccess(SEO_TAGS_PERMISSION.EDIT)

  // Without content the page still comes over — it just arrives empty, which is
  // what provisioning a new site from the sitemap needs.
  const getPageContent = (item: ExportData, withContent: boolean): string => {
    if (!withContent) {
      return ''
    }

    return item.blocks
      .filter((block) => block.parentId === item.id)
      .map((block) => {
        const texts = serializeWp(getBlockContent(block.content))

        return `<!-- wp:heading --> <h2>${block.title}</h2> <!-- /wp:heading --> ${texts}`
      })
      .join('')
  }

  const getPages = (withContent: boolean): WxrPageItem[] =>
    getFlattenExportData().map((item) => ({
      content: getPageContent(item, withContent),
      id: item.id,
      notes: item.notes ?? null,
      parentId: item.parentId,
      title: item.title,
    }))

  const handleSubmit = ({ nestUnderHome, withContent }: Options) =>
    Promise.resolve(
      new Blob(
        [
          getProjectWxr({
            authorEmail: emailAddress ?? null,
            nestUnderHome,
            pages: getPages(withContent),
            projectTitle: name,
            updatedAt,
            withSeo: hasAccess,
          }),
        ],
        { type: 'application/xml;charset=utf-8' }
      )
    )

  return { handleSubmit }
}
